import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import LoadingState from '@/components/LoadingState';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';
import { logLoginActivity } from '@/utils/activityLogger';
import { sendMagicLinkEmail } from '@/utils/magic-link/sendMagicLinkEmail';

function parseAltEmails(raw: unknown): string[] {
  if (Array.isArray(raw)) return (raw as string[]).filter(Boolean);
  if (typeof raw === 'string' && raw) return [raw];
  return [];
}

type SmsMfaFactor = { id: string; phone_number: string | null };

async function fetchActiveSmsFactor(
  userId: string,
  email: string
): Promise<SmsMfaFactor | null> {
  const { data, error } = await supabase
    .from('user_mfa_factors')
    .select('id, phone_number')
    .eq('user_id', userId)
    .eq('secret', email)
    .eq('is_active', true)
    .eq('factor_type', 'sms')
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('[ResellerAuthCallback] Error checking MFA factor:', error);
  }

  return data;
}

function hasEnrolledSmsMfa(factor: SmsMfaFactor | null): boolean {
  return Boolean(factor?.phone_number?.trim());
}

const ResellerAuthCallback = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const processAuthCallback = async () => {
    setLoading(true);
    
    try {
      // Check if there's a hash token in the URL
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      // Get token from query params for magic link flow
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const submissionIdentifier = urlParams.get('submission_identifier');
      
      // Store submission identifier in sessionStorage
      if (submissionIdentifier) {
        sessionStorage.setItem('figabl_reseller_submission_identifier', submissionIdentifier);
      }
      
      if (token) {
        // Handle magic link token
        const { error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'magiclink'
        });

        if (error) throw error;

        // Get session after successful verification
        const { data: { session } } = await supabase.auth.getSession();

        // Log the magic link login activity
        if (session?.user?.email) {
          try {
            await logLoginActivity(
              session.user.email,
              'magic_link',
              session.user.id,
              submissionIdentifier || undefined
            );
          } catch (logError) {
            console.error('[ResellerAuthCallback] Failed to log login activity:', logError);
          }
        }

        // Fetch the magic link session to get organisation info
        const { data: sessionData, error: sessionError } = await supabase
          .from('magic_link_sessions')
          .select('email, organisation_id, link_type, expires_at, alternative_email')
          .eq('submission_identifier', submissionIdentifier)
          .maybeSingle();

        if (sessionError || !sessionData) {
          console.error('[ResellerAuthCallback] Session data not found:', sessionError);
          throw new Error('Session data not found');
        }

        // Verify this is a reseller portal link
        if (sessionData.link_type !== 'reseller_portal') {
          console.error('[ResellerAuthCallback] Invalid link type:', sessionData.link_type);
          throw new Error('Invalid link type for reseller portal');
        }

        // Verify portal access has not expired
        if (sessionData.expires_at && new Date(sessionData.expires_at) < new Date()) {
          console.error('[ResellerAuthCallback] Portal access has expired:', sessionData.expires_at);
          throw new Error('This portal link has expired. Please request a new one.');
        }

        if (!sessionData.organisation_id) {
          throw new Error('Organisation ID not found in session');
        }

        // Store reseller context for MFA verification
        const recipientEmail = urlParams.get('recipient_email');
        const altEmails = parseAltEmails(sessionData.alternative_email);

        const effectiveResellerEmail =
          recipientEmail &&
          sessionData.email !== recipientEmail &&
          altEmails.includes(recipientEmail)
            ? recipientEmail
            : session?.user?.email || sessionData.email;

        sessionStorage.setItem('figabl_reseller_org_id', sessionData.organisation_id);
        sessionStorage.setItem('figabl_reseller_email', effectiveResellerEmail);

        // Check if this link was sent to an alternative email recipient

        if (
          recipientEmail &&
          session?.user?.id &&
          sessionData.email !== recipientEmail &&
          altEmails.includes(recipientEmail)
        ) {
          try {
            const { data: existingFactor } = await supabase
              .from('user_mfa_factors')
              .select('id, phone_number')
              .eq('user_id', session.user.id)
              .eq('secret', recipientEmail)
              .eq('is_active', true)
              .maybeSingle();

            if (hasEnrolledSmsMfa(existingFactor)) {
              navigate('/verify-mfa', {
                state: {
                  email: recipientEmail,
                  isAltEmailRecipient: true,
                  isResellerPortalAccess: true,
                  primaryEmail: sessionData.email,
                  organisationId: sessionData.organisation_id,
                  from: { pathname: '/client/portal' }
                }
              });
            } else {
              navigate('/setup-mfa', {
                state: {
                  email: recipientEmail,
                  isAltEmailEnrollment: true,
                  isResellerPortalAccess: true,
                  primaryEmail: sessionData.email,
                  organisationId: sessionData.organisation_id,
                  submissionIdentifier,
                  from: { pathname: '/client/portal' }
                }
              });
            }
            return;
          } catch (altEmailErr) {
            console.error('[ResellerAuthCallback] Error handling alt-email recipient flow:', altEmailErr);
            // Fall through to primary email flow
          }
        }

        // Route to MFA setup (phone entry + enroll) or verification based on enrollment
        const primaryFactor =
          session?.user?.id != null
            ? await fetchActiveSmsFactor(session.user.id, sessionData.email)
            : null;

        if (hasEnrolledSmsMfa(primaryFactor)) {
          navigate('/verify-mfa', {
            state: {
              email: sessionData.email,
              isResellerPortalAccess: true,
              organisationId: sessionData.organisation_id,
              submissionIdentifier,
              from: { pathname: '/client/portal' }
            }
          });
        } else {
          navigate('/setup-mfa', {
            state: {
              email: sessionData.email,
              isResellerPortalAccess: true,
              organisationId: sessionData.organisation_id,
              submissionIdentifier,
              from: { pathname: '/client/portal' }
            }
          });
        }
        return;
        
      } else if (accessToken && refreshToken) {
        // Handle external oauth flow
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        
        if (error) throw error;
      }

      // Fallback - shouldn't reach here normally
      setLoading(false);
      setError('Authentication flow incomplete');
      
    } catch (error) {
      console.error('[ResellerAuthCallback] Authentication error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred during authentication');
      setLoading(false);
    }
  };

  useEffect(() => {
    processAuthCallback();
  }, [navigate]);

  const handleRequestNewLink = async () => {
    try {
      setSendingLink(true);
      setLinkSent(false);

      const urlParams = new URLSearchParams(location.search);
      const submissionIdentifier = urlParams.get('submission_identifier');
      
      const { data, error } = await invokeFunction(supabase, 'get-magic-link-email', {
        body: { submissionIdentifier: submissionIdentifier || '' }
      });

      if (error) {
        throw new Error('Unable to retrieve email from the database');
      }

      if (data?.success && data.email) {
        const primaryEmail = data.email;
        const alternativeEmails = data.alternative_emails || [];
        
        const freshExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

        const { success, error: sendError } = await sendMagicLinkEmail(
          primaryEmail,
          alternativeEmails,
          { organization_id: data.organisation_id } as any,
          false,
          'reseller_portal',
          freshExpiresAt
        );
        
        if (!success || sendError) throw new Error(sendError || 'Failed to send magic link');

        setSendingLink(false);
        setLinkSent(true);

        toast({
          title: "Magic link sent",
          description: "Please check your email for the new login link."
        });
      } else {
        throw new Error(data?.error || 'Could not find email address to resend link to');
      }
    } catch (err) {
      console.error('Error sending magic link:', err);
      setSendingLink(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to send magic link"
      });
    }
  };

  if (loading) {
    return <LoadingState message="Verifying your authentication..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <main className="flex-grow flex items-center justify-center p-4">
          <div className="bg-card p-6 rounded-lg shadow-md max-w-md w-full">
            <h2 className="text-destructive text-xl font-semibold mb-4">Magic Link Expired</h2>
            <p className="text-muted-foreground mb-6">
              For security, you can only use a magic link once.
            </p>
            <Button
              onClick={handleRequestNewLink}
              className="w-full mb-4 flex items-center justify-center"
              disabled={sendingLink || linkSent}
            >
              <Mail className="mr-2 h-4 w-4" />
              {sendingLink
                ? 'Sending Magic Link...'
                : linkSent
                ? 'Magic Link Sent'
                : 'Resend Magic Link'}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return <LoadingState message="Redirecting..." />;
};

export default ResellerAuthCallback;
